// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { ArrowUp, ArrowDown, Crosshair, Plane, PersonStanding, Swords, Volume2, VolumeX, Car, Globe, Puzzle, Save, Link, Copy, Check, Loader2, LogIn, LogOut, Pause, Play, CreditCard } from 'lucide-react';

import RunnerMode from './components/RunnerMode';
import FlightMode from './components/FlightMode';
import CombatMode from './components/CombatMode';
import RacerMode from './components/RacerMode';
import OpenWorldMode from './components/OpenWorldMode';
import PuzzlerMode from './components/PuzzlerMode';
import QuaylaLoader from './components/QuaylaLoader';
import PostGameSummary from './components/PostGameSummary';
import SharePanel from './components/SharePanel';
import musicEngine from './MusicEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const QUAYLA_URL = 'https://quayla.live';

// Axios interceptor: attach Quayla token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('quayla_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

function App() {
  // Prompts (empty by default, Runner placeholders shown as hints)
  const [gameTitle, setGameTitle] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [envPrompt, setEnvPrompt] = useState('');
  const [charPrompt, setCharPrompt] = useState('');
  const [enemyPrompt, setEnemyPrompt] = useState('');
  const [obstaclePrompt, setObstaclePrompt] = useState('');
  
  // Game settings (before generate)
  const [gameMode, setGameMode] = useState('run'); // 'run', 'fly', 'combat'

  // Pre-prompt defaults per mode (shown as placeholders)
  const modeDefaults = {
    run: { title: 'Holey Moley', name: 'Quayla', env: 'A Sci Fi Futuristic Dirty City Made For Moles', char: 'A Mole With An Orange Construction Hat On', enemy: 'Fierce Guard Dogs', obstacle: 'Brick Walls' },
    fly: { title: 'Neon Drift', name: 'Quayla', env: 'A neon-lit Tokyo cyberpunk district at midnight with rain', char: 'A sleek android with glowing blue circuits', enemy: 'Rogue police drones with red searchlights', obstacle: 'Holographic barriers and electric fences' },
  };

  const selectMode = useCallback((modeId) => {
    setGameMode(modeId);
  }, []);

  // Get placeholders for current mode
  const getPlaceholder = (field) => {
    const defaults = modeDefaults[gameMode];
    if (!defaults) return { title: 'My Awesome Game...', name: 'Enter your name...', env: 'Cyberpunk city, space station, volcano...', char: 'Robot warrior, dragon rider, ninja...', enemy: 'Alien swarm, shadow demons, drones...', obstacle: 'Brick walls, ramps, street signs, barrels...' }[field];
    return defaults[field] || '';
  };

  // Quayla auto-generate — generates truly random game concepts each time
  const quaylaSettings = [
    'A haunted medieval castle with floating candles and cobweb corridors',
    'An underwater coral kingdom with bioluminescent jellyfish',
    'A frozen tundra with ice caves and aurora borealis skies',
    'A volcanic island with rivers of magma and obsidian cliffs',
    'A steampunk airship city floating above the clouds',
    'A bamboo forest temple guarded by ancient stone golems',
    'A neon-drenched cyberpunk alleyway in a rainy megacity',
    'A candy wonderland with chocolate rivers and gummy mountains',
    'A desert oasis with golden sand dunes and crystal pyramids',
    'A space station orbiting a dying red giant star',
    'A mystical mushroom forest with glowing spores and fog',
    'A ruined library dimension where books come alive',
    'A tropical pirate cove with shipwrecks and hidden treasure',
    'A clockwork mechanical world with brass gears everywhere',
    'A crystal cave deep underground with prismatic light beams',
  ];
  const quaylaCharacters = [
    'A brave fox warrior with a enchanted sword',
    'A tiny robot with rocket boots and a heart of gold',
    'A space pirate cat with an eyepatch and jetpack',
    'A young wizard apprentice with a glowing staff',
    'A mischievous raccoon in a leather jacket',
    'A cybernetic ninja with holographic throwing stars',
    'An adventurous penguin wearing a tiny explorer hat',
    'A fire-breathing baby dragon with golden scales',
    'A samurai hamster with a katana bigger than itself',
    'A ghost hunter kid with a proton backpack',
    'A time-traveling dinosaur in a lab coat',
    'A skateboarding alien with three eyes',
    'A mushroom knight with a cork shield',
    'A deep sea diver octopus with gadget tentacles',
    'A cosmic owl wizard that glows in the dark',
  ];
  const quaylaEnemies = [
    'Shadow wraiths that phase through walls',
    'Giant mechanical spiders with laser eyes',
    'Swarms of angry fire wasps',
    'Ice golems that shatter and reform',
    'Dark sorcerers hurling energy bolts',
    'Mutant rats with razor teeth and steel tails',
    'Flying gargoyles that dive bomb from above',
    'Toxic slime creatures that split when hit',
    'Phantom pirates with ghost swords',
    'Rogue battle drones with plasma cannons',
    'Crystal monsters that reflect attacks',
    'Zombie Vikings with enchanted axes',
    'Shapeshifting shadow demons',
    'Electric eels that shoot lightning',
    'Ancient cursed knights in rusted armor',
  ];
  const quaylaObstacles = [
    'Collapsing platforms and spike pits',
    'Laser grid barriers and force fields',
    'Rolling boulders and swinging axes',
    'Toxic gas clouds and acid pools',
    'Crumbling bridges and falling debris',
    'Electrified fences and tesla coils',
    'Ice patches and frozen geysers',
    'Lava jets and fire walls',
    'Vine traps and quicksand',
    'Wind tunnels and gravity wells',
  ];
  const quaylaTitles = [
    'Shadow Quest', 'Neon Fury', 'Cosmic Clash', 'Crystal Reign', 'Phantom Chase',
    'Blaze Runner', 'Storm Forge', 'Void Walker', 'Iron Fang', 'Star Breaker',
    'Ghost Protocol', 'Magma Rush', 'Frost Bite', 'Dark Circuit', 'Thunder Paw',
    'Pixel Storm', 'Nova Strike', 'Wild Surge', 'Echo Hunt', 'Prism Dash',
    'Turbo Glide', 'Venom Trail', 'Astro Blitz', 'Lunar Drift', 'Ember Quest',
  ];

  const letQuaylaGenerate = useCallback(() => {
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    setGameTitle(pick(quaylaTitles));
    setPlayerName('');
    setEnvPrompt(pick(quaylaSettings));
    setCharPrompt(pick(quaylaCharacters));
    setEnemyPrompt(pick(quaylaEnemies));
    setObstaclePrompt(pick(quaylaObstacles));
    // Trigger generation after a short delay so state is updated
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="generate-btn"]');
      if (btn) btn.click();
    }, 300);
  }, []);
  
  // Scene data
  const [sceneData, setSceneData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState(null);
  
  // Game state
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [gameKey, setGameKey] = useState(0); // used to force restart
  const [gameOver, setGameOver] = useState(false);
  const [showPostGameSummary, setShowPostGameSummary] = useState(false);
  const [isVictory, setIsVictory] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);

  // Save game
  const [savedGameUrl, setSavedGameUrl] = useState(null);
  const [savingGame, setSavingGame] = useState(false);
  const [copied, setCopied] = useState(false);

  // Music
  const [musicData, setMusicData] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicLoading, setMusicLoading] = useState(false);

  // Quayla SSO
  const [quaylaUser, setQuaylaUser] = useState(null); // { user, credits, subscription, entitlements }
  const [authLoading, setAuthLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeUrl, setUpgradeUrl] = useState('');

  // Pause
  const [isPaused, setIsPaused] = useState(false);

  // Exit confirmation
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Story scene images (generated in background during gameplay)
  const [storyImages, setStoryImages] = useState({});
  const [dynamicStories, setDynamicStories] = useState(null);
  const storyGenQueueRef = useRef(new Set());

  // Story prompts for each Holey Moley level
  const storyScenePrompts = [
    "A cartoon mole character with an orange construction hat relaxing on a beat-up couch inside a cozy dirty underground apartment, warm lamp light, mud walls, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat pushing a tiny shopping cart through an underground grocery store, shelves with canned worms and beetle chips, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat sitting at a bar counter in a dimly lit underground pub, clinking mugs with other moles, warm lighting, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat wearing welding goggles in an underground workshop, sparks flying from tools, gadgets everywhere, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat reading a large book in an underground library, dusty shelves, old maps on walls, lantern light, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat relaxing in a steaming mud spa pool underground, steam rising, peaceful, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat standing at a glowing holographic map table in an underground command center, screens and buttons, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat sitting on a hospital bed in an underground clinic, bandages, a friendly mole doctor, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat lifting tiny weights in an underground gym, sweating, workout equipment, cartoon style, game cutscene art",
    "A cartoon mole character with an orange construction hat sitting on a golden throne in a luxurious underground penthouse, velvet curtains, gold decorations, cartoon style, game cutscene art",
  ];

  const generateStoryImage = useCallback(async (stageNum) => {
    const idx = Math.min(stageNum - 1, 9);
    if (storyGenQueueRef.current.has(idx)) return;
    storyGenQueueRef.current.add(idx);
    try {
      // Use dynamic story image_prompt if available, else Holey Moley fallback for Runner only
      let prompt;
      if (dynamicStories && dynamicStories[idx] && dynamicStories[idx].image_prompt) {
        prompt = dynamicStories[idx].image_prompt;
      } else if (gameMode === 'run' && storyScenePrompts[idx]) {
        prompt = storyScenePrompts[idx];
      } else {
        return; // No prompt available yet — will be generated when dynamicStories loads
      }
      const startRes = await axios.post(`${API}/generate-image-async`, { prompt }, { timeout: 15000 });
      const jobId = startRes.data.job_id;
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const pollRes = await axios.get(`${API}/job-status/${jobId}`, { timeout: 10000 });
          if (pollRes.data.status === 'completed') {
            setStoryImages(prev => ({ ...prev, [idx]: `data:image/png;base64,${pollRes.data.image_base64}` }));
            return;
          }
        } catch { /* retry */ }
      }
    } catch (err) {
      console.warn('Story image generation failed:', err.message);
    }
  }, [dynamicStories, gameMode]);

  // When dynamicStories load, start generating story image for stage 1 if not done
  useEffect(() => {
    if (dynamicStories && dynamicStories.length > 0 && isPlaying) {
      if (!storyGenQueueRef.current.has(0)) {
        generateStoryImage(1);
      }
    }
  }, [dynamicStories, isPlaying, generateStoryImage]);

  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      const newVal = !prev;
      window.gamePaused = newVal;
      return newVal;
    });
  }, []);

  // Mode display names (used in game screen, game over overlay, and start screen)
  const modeNames = { run: 'ENDLESS RUNNER STYLE GAME', fly: 'ACE COMBAT', combat: 'STREET FIGHTER', race: 'RACER', openworld: 'OPEN WORLD', puzzle: 'PUZZLER' };


  const restartGame = useCallback(() => {
    setScore(0);
    setGameOver(false);
    setShowPostGameSummary(false);
    setIsVictory(false);
    setScreenshotDataUrl(null);
    setIsPaused(false);
    setShowExitConfirm(false);
    window.gamePaused = false;
    setGameKey(k => k + 1);
  }, []);

  // ── Quayla SSO: Handle access_token from redirect ──
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#access_token=')) {
      const tok = decodeURIComponent(hash.split('=')[1]);
      localStorage.setItem('quayla_token', tok);
      sessionStorage.removeItem('sso_attempted');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // ── Verify stored token on mount, auto-redirect if no token ──
  useEffect(() => {
    const token = localStorage.getItem('quayla_token');
    if (token) {
      // Have a token — verify it
      axios.get(`${API}/auth/me`)
        .then(res => {
          setQuaylaUser(res.data);
          setAuthLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('quayla_token');
          setQuaylaUser(null);
          setAuthLoading(false);
          // Token was invalid — try SSO once
          if (!sessionStorage.getItem('sso_attempted')) {
            sessionStorage.setItem('sso_attempted', '1');
            const returnUrl = window.location.origin + window.location.pathname;
            window.location.href = `${QUAYLA_URL}/?sso_redirect=${encodeURIComponent(returnUrl)}`;
          }
        });
    } else {
      // No token at all — auto-redirect to Quayla SSO (once per session)
      if (!sessionStorage.getItem('sso_attempted')) {
        sessionStorage.setItem('sso_attempted', '1');
        const returnUrl = window.location.origin + window.location.pathname;
        window.location.href = `${QUAYLA_URL}/?sso_redirect=${encodeURIComponent(returnUrl)}`;
        return; // Don't set authLoading false, we're navigating away
      }
      setAuthLoading(false);
    }
  }, []);

  const loginWithQuayla = useCallback(() => {
    const returnUrl = window.location.origin + window.location.pathname;
    window.location.href = `${QUAYLA_URL}/?sso_redirect=${encodeURIComponent(returnUrl)}`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('quayla_token');
    sessionStorage.removeItem('sso_attempted');
    setQuaylaUser(null);
  }, []);

  const isAdmin = quaylaUser?.user?.role === 'admin' || quaylaUser?.user?.role === 'master';
  const isLoggedIn = !!quaylaUser;

  // ── URL Param Handlers (saved game load) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameId = params.get('game');

    if (gameId) {
      loadSavedGame(gameId);
    }
  }, []);

  const loadSavedGame = async (gameId) => {
    setLoading(true);
    setLoadingStatus('Loading saved game...');
    try {
      const res = await axios.get(`${API}/games/${gameId}`);
      setSceneData(res.data.scene_data);
      setGameMode(res.data.game_mode || 'run');
      setGameTitle(res.data.game_title || '');
      if (res.data.prompts) {
        setEnvPrompt(res.data.prompts.environment || '');
        setCharPrompt(res.data.prompts.character || '');
        setEnemyPrompt(res.data.prompts.enemies || '');
      }
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      setError('Failed to load saved game. It may no longer exist.');
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  };

  const saveGame = async () => {
    if (!sceneData || savingGame) return;
    setSavingGame(true);
    try {
      const res = await axios.post(`${API}/games/save`, {
        scene_data: sceneData,
        game_mode: gameMode,
        game_title: gameTitle,
        prompts: {
          environment: envPrompt,
          character: charPrompt,
          enemies: enemyPrompt,
        },
      });
      const shareUrl = `${window.location.origin}?game=${res.data.id}`;
      setSavedGameUrl(shareUrl);
    } catch (err) {
      setError('Failed to save game');
    } finally {
      setSavingGame(false);
    }
  };

  const copyShareLink = useCallback(() => {
    if (!savedGameUrl) return;
    navigator.clipboard.writeText(savedGameUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [savedGameUrl]);

  // Hero video
  const heroVideoRef = useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [showDirections, setShowDirections] = useState(false);

  // Game speed for Runner mode
  const [gameSpeed, setGameSpeed] = useState('medium');

  const initVideo = useCallback((videoEl) => {
    if (!videoEl) return;
    heroVideoRef.current = videoEl;
    videoEl.muted = true;
    
    const startPlay = () => {
      videoEl.play().then(() => {
        setVideoReady(true);
      }).catch(() => {});
    };
    
    // Play when data is ready
    videoEl.addEventListener('canplay', startPlay, { once: true });
    
    // Fallback: if already ready or cached
    if (videoEl.readyState >= 3) startPlay();
    
    // Safety fallback: fade logo after 5 seconds regardless
    setTimeout(() => {
      if (!heroVideoRef.current?.paused) setVideoReady(true);
    }, 5000);
  }, []);

  const toggleMute = useCallback(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    if (v.muted) {
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => { v.muted = true; });
      setVideoMuted(false);
      setVideoEnded(false);
    } else {
      v.muted = true;
      setVideoMuted(true);
    }
  }, []);

  const generateScene = useCallback(async () => {
    // Use placeholder defaults if fields are empty
    const defaults = modeDefaults[gameMode] || {};
    const effectiveEnv = envPrompt || defaults.env || '';
    const effectiveChar = charPrompt || defaults.char || '';
    const effectiveEnemy = enemyPrompt || defaults.enemy || '';
    const effectiveTitle = gameTitle || defaults.title || 'Planet Q Game';
    const effectiveObstacle = obstaclePrompt || defaults.obstacle || '';
    const effectiveName = playerName || defaults.name || '';

    if (!effectiveEnv && !effectiveChar && !effectiveEnemy) {
      setError('Please enter at least one prompt to generate a scene');
      return;
    }

    // Set effective values so they carry through to the game
    if (!gameTitle && defaults.title) setGameTitle(defaults.title);
    if (!playerName && defaults.name) setPlayerName(defaults.name);
    if (!envPrompt && defaults.env) setEnvPrompt(defaults.env);
    if (!charPrompt && defaults.char) setCharPrompt(defaults.char);
    if (!enemyPrompt && defaults.enemy) setEnemyPrompt(defaults.enemy);
    if (!obstaclePrompt && defaults.obstacle) setObstaclePrompt(defaults.obstacle);

    setLoading(true);
    setError(null);

    // ── Charge credits via Quayla (admin/master = free) ──
    if (!isAdmin) {
      try {
        setLoadingStatus('Checking credits...');
        await axios.post(`${API}/auth/charge-generation`);
      } catch (err) {
        if (err.response?.status === 402) {
          const detail = err.response?.data?.detail || {};
          setUpgradeUrl(detail.upgrade_url || `${QUAYLA_URL}/billing/upgrade`);
          setShowUpgradeModal(true);
          setLoading(false);
          setLoadingStatus('');
          return;
        }
        if (err.response?.status === 403) {
          const detail = err.response?.data?.detail || {};
          setUpgradeUrl(detail.upgrade_url || `${QUAYLA_URL}/billing/upgrade`);
          setShowUpgradeModal(true);
          setLoading(false);
          setLoadingStatus('');
          return;
        }
        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
          logout();
          setLoading(false);
          setLoadingStatus('');
          return;
        }
        setError('Failed to process credits. Please try again.');
        setLoading(false);
        setLoadingStatus('');
        return;
      }
    }

    // ── Generate images ──
    try {
      const mode = gameMode;
      const modeBg = {
        run: `Endless runner game environment of ${effectiveEnv}. Third-person perspective looking down a long straight road stretching into the distance, vanishing point at center, scenic environment on both sides, immersive depth, atmospheric, cinematic game art, high detail`,
        fly: `Aerial view of ${effectiveEnv} from above, vast sky with clouds, cinematic flight game environment, high detail, dramatic lighting`,
        race: `First-person cockpit view of a race track through ${effectiveEnv}. Road stretching ahead, racing circuit, cinematic, high detail`,
        openworld: `Lush detailed environment of ${effectiveEnv}. Third-person adventure game landscape, dense scenery, atmospheric lighting, high detail`,
        puzzle: `Colorful abstract artistic background inspired by ${effectiveEnv}. Vibrant colors, puzzle game aesthetic, high detail`,
        combat: `Wide side-view fighting arena stage of ${effectiveEnv}. Ground and floor visible in the lower third of the image, environment and sky filling the upper portion. Flat ground surface, dramatic lighting, fighting game background, high detail, full scene`,
      };
      const modeChar = {
        run: `Single character sprite of ${effectiveChar} seen from behind (back view). Full body, running pose, transparent background, game character art`,
        fly: `Single flying vehicle sprite of ${effectiveChar} seen from behind. Transparent background, game sprite art`,
        race: `Single race car sprite of ${effectiveChar} seen from behind. Transparent background, racing game art`,
        openworld: `Single adventure character sprite of ${effectiveChar} seen from behind. Full body, transparent background, RPG character art`,
        puzzle: `Single cute character mascot of ${effectiveChar}. Full body, front view, colorful, transparent background`,
        combat: `Full body side view fighting game sprite of ${effectiveChar}, exactly 90 degree side profile, character facing RIGHT. Classic 2D fighting game pose with fists up. Full body head to toes visible. Transparent background, high detail`,
      };
      const modeEnemy = {
        run: `Single enemy creature sprite of ${effectiveEnemy}. Front-facing, menacing, transparent background, game enemy art`,
        fly: `Single enemy aircraft sprite of ${effectiveEnemy}. Front-facing, transparent background, game enemy sprite art`,
        race: `Single rival vehicle sprite of ${effectiveEnemy}. Rear view, transparent background, racing game art`,
        openworld: `Single enemy monster sprite of ${effectiveEnemy}. Front-facing, menacing, transparent background, RPG enemy art`,
        puzzle: `Single colorful game obstacle sprite of ${effectiveEnemy}. Transparent background, puzzle game art`,
        combat: `Full body side view fighting game sprite of ${effectiveEnemy}, exactly 90 degree side profile, character facing RIGHT. Classic 2D fighting game villain pose with fists up. Full body head to toes visible. Transparent background, high detail`,
      };

      // Helper: generate image via async job + polling (avoids proxy timeout)
      const generateImage = async (prompt, transparent = false) => {
        const startRes = await axios.post(`${API}/generate-image-async`, { prompt, transparent }, { timeout: 15000 });
        const jobId = startRes.data.job_id;
        for (let i = 0; i < 90; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const pollRes = await axios.get(`${API}/job-status/${jobId}`, { timeout: 10000 });
            if (pollRes.data.status === 'completed') {
              return pollRes.data.image_base64;
            }
          } catch (pollErr) {
            if (pollErr.response?.status === 500) {
              throw new Error(pollErr.response?.data?.detail || 'Image generation failed');
            }
          }
        }
        throw new Error('Image generation timed out');
      };

      // Generate ALL images in PARALLEL for fast start (3x speedup)
      setLoadingStatus('Generating your world — background, character & enemies...');
      setLoadingStep(0);

      const imagePromises = [
        generateImage(modeBg[mode] || modeBg.run, false),
        generateImage(modeChar[mode] || modeChar.run, true),
        generateImage(modeEnemy[mode] || modeEnemy.run, true),
      ];

      // Obstacle in parallel too (optional, won't fail the batch)
      let obstaclePromise = null;
      if (effectiveObstacle && effectiveObstacle.trim()) {
        obstaclePromise = generateImage(
          `Collection of stationary obstacles: ${effectiveObstacle}. Game sprite sheet, transparent background, top-down perspective game obstacles, clean edges`,
          true
        ).catch(() => null);
      }

      // Wait for all images simultaneously
      const [bgBase64, charBase64, enemyBase64] = await Promise.all(imagePromises);
      setLoadingStep(2);
      const obstacleBase64 = obstaclePromise ? await obstaclePromise : null;

      // Quayla encouraging TTS (quick, non-blocking)
      setLoadingStatus('Almost there! Putting it all together...');
      const quaylaFinishPhrases = [
        "Brilliant! Your game is almost ready, it's going to be spectacular!",
        "Oh lovely, this is coming together beautifully! Nearly there!",
        "Right then, just finishing up the final touches. You're going to love this!",
        "This is proper exciting! Your world is nearly complete!",
        "Almost done! I reckon this one's going to be absolutely brilliant!",
        "Smashing! Just a few more moments and your game will be ready to play!",
        "Looking gorgeous! Nearly finished, just polishing up the details!",
      ];
      let firstAudioDone = Promise.resolve();
      try {
        const phrase = quaylaFinishPhrases[Math.floor(Math.random() * quaylaFinishPhrases.length)];
        const ttsRes = await axios.post(`${API}/generate-quayla-speech`, { text: phrase }, { timeout: 15000 });
        if (ttsRes.data?.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${ttsRes.data.audio_base64}`);
          audio.volume = 0.7;
          audio.play().catch(() => {});
          firstAudioDone = new Promise(r => { audio.onended = r; setTimeout(r, 12000); });
        }
      } catch {
        // TTS is optional
      }

      // Start game immediately — Sora 2 video generates in background
      setSceneData({
        id: Date.now().toString(),
        background_image: `data:image/png;base64,${bgBase64}`,
        character_image: `data:image/png;base64,${charBase64}`,
        enemy_image: `data:image/png;base64,${enemyBase64}`,
        obstacle_image: obstacleBase64 ? `data:image/png;base64,${obstacleBase64}` : null,
        video_url: null,
        prompts: { environment: effectiveEnv, character: effectiveChar, enemies: effectiveEnemy, obstacles: effectiveObstacle },
        created_at: new Date().toISOString(),
      });

      // Refresh credits after generation
      try {
        const meRes = await axios.get(`${API}/auth/me`);
        setQuaylaUser(meRes.data);
      } catch { /* non-critical */ }

      // Wait for first Quayla voice to finish before speaking again
      await firstAudioDone;

      // Quayla announces the game is ready
      const quaylaDonePhrases = [
        "Your game is ready! Go on then, have a brilliant time!",
        "All done! Your world is ready to play, I hope you love it!",
        "Lovely! Your game is finished, it looks absolutely smashing!",
        "Right, your game is all set! Get in there and show them what you've got!",
        "Ta-da! Your game is ready to play, I'm proper excited for you!",
        "Brilliant work! Everything is set up and ready for you to enjoy!",
        "Your world is complete! It turned out gorgeous, if I do say so myself!",
      ];
      try {
        const donePhrase = quaylaDonePhrases[Math.floor(Math.random() * quaylaDonePhrases.length)];
        const ttsDone = await axios.post(`${API}/generate-quayla-speech`, { text: donePhrase }, { timeout: 15000 });
        if (ttsDone.data?.audio_base64) {
          const doneAudio = new Audio(`data:audio/mp3;base64,${ttsDone.data.audio_base64}`);
          doneAudio.volume = 0.7;
          doneAudio.play().catch(() => {});
        }
      } catch {
        // TTS is optional
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to generate scene: ${detail}`);
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  }, [envPrompt, charPrompt, enemyPrompt, obstaclePrompt, gameMode, gameTitle, isAdmin, logout]);

  const resetScene = useCallback(() => {
    setEnvPrompt('');
    setCharPrompt('');
    setEnemyPrompt('');
    setObstaclePrompt('');
    setSceneData(null);
    setError(null);
    setIsPlaying(false);
    setScore(0);
    setSavedGameUrl(null);
    musicEngine.stop();
    setMusicPlaying(false);
    setMusicData(null);
  }, []);

  const startGame = useCallback(() => {
    setIsPlaying(true);
    setScore(0);
    setGameOver(false);
    // Reset stories & images for this new game (each game gets its own)
    setDynamicStories(null);
    setStoryImages({});
    storyGenQueueRef.current = new Set();
    // Generate unique story scenes for this game using GPT-4o
    if (gameMode === 'run' || gameMode === 'fly') {
      axios.post(`${API}/generate-story-scenes`, {
        game_title: gameTitle || 'Unnamed Game',
        character_prompt: charPrompt || '',
        environment_prompt: envPrompt || '',
        enemies_prompt: enemyPrompt || '',
        obstacles_prompt: obstaclePrompt || '',
      }).then(res => {
        if (res.data.scenes) {
          setDynamicStories(res.data.scenes);
        }
      }).catch(err => {
        console.warn('Story scene generation failed, using fallback:', err.message);
      });
      // Start generating the first story image right away
      generateStoryImage(1);
    }
    // Generate and start music at 25% volume when game begins
    musicEngine.setVolume(0.25);
    if (!musicData && !musicLoading) {
      setMusicLoading(true);
      axios.post(`${API}/generate-music`, {
        environment_prompt: envPrompt || 'fantasy world',
        character_prompt: charPrompt || 'hero',
        enemies_prompt: enemyPrompt || 'monsters',
        game_mode: gameMode,
        game_title: gameTitle,
      }).then(res => {
        setMusicData(res.data);
        musicEngine.play(res.data);
        musicEngine.setVolume(0.25);
        setMusicPlaying(true);
      }).catch(err => {
        console.warn('Music generation failed:', err);
      }).finally(() => {
        setMusicLoading(false);
      });
    } else if (musicData) {
      musicEngine.play(musicData);
      musicEngine.setVolume(0.25);
      setMusicPlaying(true);
    }
  }, [musicData, musicLoading, envPrompt, charPrompt, enemyPrompt, gameMode, gameTitle]);

  const toggleMusic = useCallback(() => {
    if (musicPlaying) {
      musicEngine.stop();
      setMusicPlaying(false);
    } else if (musicData) {
      musicEngine.play(musicData);
      setMusicPlaying(true);
    }
  }, [musicPlaying, musicData]);

  const handleJump = useCallback(() => {
    if (window.gameActions?.jump) window.gameActions.jump();
  }, []);

  const handleShoot = useCallback(() => {
    if (window.gameActions?.shoot) window.gameActions.shoot();
  }, []);

  const handleMoveUp = useCallback(() => {
    if (window.gameActions?.moveUp) window.gameActions.moveUp();
  }, []);

  const handleMoveDown = useCallback(() => {
    if (window.gameActions?.moveDown) window.gameActions.moveDown();
  }, []);

  const handleGameOver = useCallback((finalScore, victory = false) => {
    setScore(finalScore);
    setGameOver(true);
    setIsVictory(victory);
    musicEngine.stop();
    setMusicPlaying(false);

    // Capture canvas screenshot for sharing
    try {
      const canvas = document.querySelector('canvas');
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        setScreenshotDataUrl(dataUrl);
      }
    } catch {}

    // Show post-game summary after a brief delay
    setTimeout(() => {
      setShowPostGameSummary(true);
    }, 1500);
  }, []);

  const backToMenu = useCallback(() => {
    if (isAdmin) {
      setIsPlaying(false);
      setGameOver(false);
      setShowPostGameSummary(false);
      setIsVictory(false);
      setScreenshotDataUrl(null);
      setSceneData(null);
      setIsPaused(false);
      window.gamePaused = false;
      musicEngine.stop();
      setMusicPlaying(false);
      return;
    }
    setShowExitConfirm(true);
  }, [isAdmin]);

  const confirmExit = useCallback(() => {
    setShowExitConfirm(false);
    setIsPlaying(false);
    setGameOver(false);
    setShowPostGameSummary(false);
    setIsVictory(false);
    setScreenshotDataUrl(null);
    setSceneData(null);
    setIsPaused(false);
    window.gamePaused = false;
    musicEngine.stop();
    setMusicPlaying(false);
    setMusicData(null);
  }, []);

  // Pick the right game component based on mode
  const getGameComponent = () => {
    switch (gameMode) {
      case 'run': return RunnerMode;
      case 'fly': return FlightMode;
      case 'combat': return CombatMode;
      case 'race': return RacerMode;
      case 'openworld': return OpenWorldMode;
      case 'puzzle': return PuzzlerMode;
      default: return RunnerMode;
    }
  };

  // ── FULL-SCREEN START SCREEN (after generation, before playing) ──
  if (sceneData && !isPlaying && !loading) {
    return (
      <div className="fixed inset-0 bg-black" data-testid="start-screen">
        {/* Full-screen background */}
        {sceneData.background_image && (
          <img
            src={sceneData.background_image}
            alt="Game World"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Dark overlay for readability */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.6) 100%)' }} />

        {/* Header */}
        <div className="absolute top-0 left-0 right-0 text-center pt-4 z-10">
          <h1
            className="text-xl md:text-2xl font-bold text-white tracking-wide"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            PLANET Q AI VIDEO GAMES
          </h1>
          <p className="text-cyan-400 text-xs tracking-wider">BY PLANET Q PRODUCTIONS</p>
        </div>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          {/* Game Title */}
          <h2
            className="text-5xl md:text-7xl font-bold text-white mb-2 text-center px-4"
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              textShadow: '0 0 30px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.8)',
            }}
            data-testid="start-screen-title"
          >
            {gameTitle || 'PLANET Q'}
          </h2>
          <p
            className="text-cyan-400 text-sm md:text-base uppercase tracking-[0.3em] mb-2"
            style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 15px rgba(0,240,255,0.5)' }}
          >
            BY QUAYLA
          </p>
          {playerName ? (
            <p
              className="text-white/50 text-xs uppercase tracking-[0.2em] mb-8"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
              data-testid="start-screen-player-name"
            >
              PRODUCED FOR {playerName}
            </p>
          ) : (
            <div className="mb-8" />
          )}

          {/* Character & Enemy Sprites */}
          <div className="flex items-end gap-8 md:gap-16 mb-10">
            {sceneData.character_image && (
              <img
                src={sceneData.character_image}
                alt="Character"
                className="w-28 h-28 md:w-36 md:h-36 object-contain"
                style={{ filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.5))' }}
                data-testid="start-screen-character"
              />
            )}
            {sceneData.enemy_image && (
              <img
                src={sceneData.enemy_image}
                alt="Enemy"
                className="w-24 h-24 md:w-32 md:h-32 object-contain"
                style={{ filter: 'drop-shadow(0 0 15px rgba(255,0,0,0.5))', transform: 'scaleX(-1)' }}
                data-testid="start-screen-enemy"
              />
            )}
          </div>

          {/* Speed Selection (Runner mode only) */}
          {gameMode === 'run' && (
            <div className="flex gap-3 mb-6" data-testid="speed-selector">
              {[
                { id: 'slow', label: 'SLOW', color: '#00FF88' },
                { id: 'medium', label: 'MEDIUM', color: '#FFD700' },
                { id: 'fast', label: 'FAST', color: '#FF4444' },
              ].map((sp) => (
                <button
                  key={sp.id}
                  data-testid={`speed-${sp.id}`}
                  onClick={() => setGameSpeed(sp.id)}
                  className="px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all"
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    background: gameSpeed === sp.id ? `${sp.color}30` : 'rgba(255,255,255,0.08)',
                    border: `2px solid ${gameSpeed === sp.id ? sp.color : 'rgba(255,255,255,0.15)'}`,
                    color: gameSpeed === sp.id ? sp.color : 'rgba(255,255,255,0.5)',
                    boxShadow: gameSpeed === sp.id ? `0 0 15px ${sp.color}30` : 'none',
                  }}
                >
                  {sp.label}
                </button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={startGame}
              data-testid="play-now-btn"
              className="px-10 py-4 rounded-full text-white font-bold text-lg uppercase tracking-wider flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #00F0FF 0%, #0088AA 100%)',
                fontFamily: "'Rajdhani', sans-serif",
                boxShadow: '0 4px 20px rgba(0,240,255,0.4)',
              }}
            >
              <span style={{ fontSize: '1.2em' }}>{'\u25B6'}</span> PLAY NOW
            </button>
            <button
              onClick={saveGame}
              disabled={savingGame}
              data-testid="save-preview-btn"
              className="px-8 py-4 rounded-full text-white font-bold text-lg uppercase flex items-center gap-2"
              style={{
                background: 'rgba(0,150,255,0.5)',
                backdropFilter: 'blur(8px)',
                fontFamily: "'Rajdhani', sans-serif",
                boxShadow: '0 4px 15px rgba(0,100,200,0.3)',
              }}
            >
              {savingGame ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              SAVE
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-3 left-0 right-0 text-center z-10">
          <p className="text-white/30 text-xs">© Planet Q Productions</p>
        </div>

        {/* Share Link Modal */}
        {savedGameUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="p-6 rounded-xl max-w-sm w-full mx-4" style={{ background: '#0a1628', border: '1px solid rgba(0,240,255,0.3)' }} data-testid="share-modal-start">
              <div className="flex items-center gap-2 mb-3">
                <Link className="w-5 h-5 text-cyan-400" />
                <h3 className="text-white font-bold">Game Saved!</h3>
              </div>
              <p className="text-gray-400 text-sm mb-3">Share this link to let others play your game:</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={savedGameUrl}
                  className="flex-1 px-3 py-2 rounded-md text-white text-xs"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <button
                  onClick={copyShareLink}
                  className="px-3 py-2 rounded-md text-white text-sm"
                  style={{ background: 'rgba(0,240,255,0.2)', border: '1px solid rgba(0,240,255,0.4)' }}
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={() => setSavedGameUrl(null)}
                className="mt-4 w-full py-2 rounded-lg text-white text-sm font-bold"
                style={{ background: 'rgba(255,255,255,0.1)' }}
              >
                CLOSE
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Active Game
  if (isPlaying && sceneData) {
    const GameComponent = getGameComponent();
    
    // Mode-specific control labels
    const modeControls = {
      run: { up: '', left: '←', right: '→', down: '', actions: ['JUMP'] },
      fly: { up: 'UP', left: '←', right: '→', down: 'DOWN', actions: ['FIRE'] },
      combat: { up: 'JUMP', left: '←', right: '→', down: 'BLOCK', actions: [], combatButtons: true },
      race: { up: 'GAS', left: '←', right: '→', down: 'BRAKE', actions: ['BOOST'] },
      openworld: { up: 'UP', left: '←', right: '→', down: 'DOWN', actions: ['INTERACT'] },
      puzzle: { up: '↑', left: '←', right: '→', down: '↓', actions: [] },
    };
    const ctrl = modeControls[gameMode] || modeControls.run;

    return (
      <div className="fixed inset-0 bg-black" data-testid="game-screen">
        <GameComponent
          key={gameKey}
          backgroundImage={sceneData.background_image}
          characterImage={sceneData.character_image}
          enemyImage={sceneData.enemy_image}
          obstacleImage={sceneData.obstacle_image}
          videoUrl={sceneData.video_url}
          gameTitle={gameTitle || 'PLANET Q'}
          gameSpeed={gameSpeed}
          storyImages={storyImages}
          dynamicStories={dynamicStories}
          isPlaying={isPlaying}
          onScoreUpdate={setScore}
          onGameOver={handleGameOver}
          onStageComplete={async (stageNum) => {
            // Start generating the NEXT story image in background
            if (gameMode === 'run' || gameMode === 'fly') {
              generateStoryImage(stageNum);
            }
            // Generate new background for next stage using async job pattern
            try {
              const variations = ['at sunset', 'at night', 'in a storm', 'in winter', 'on fire', 'underwater', 'in space', 'at dawn', 'during an eclipse', 'in a neon city'];
              const variation = variations[stageNum % variations.length];
              const bgPrompt = gameMode === 'fly'
                ? `Aerial view of ${envPrompt || 'sky'} ${variation}. Vast sky with clouds, cinematic flight game environment, dramatic lighting, high detail`
                : `Endless runner game environment of ${envPrompt || 'fantasy landscape'} ${variation}. Third-person perspective looking down a long road, vanishing point, cinematic game art, high detail`;
              const bgRes = await axios.post(`${API}/generate-image-async`, { prompt: bgPrompt });
              const jobId = bgRes.data.job_id;
              // Poll for completion
              for (let i = 0; i < 60; i++) {
                await new Promise(r => setTimeout(r, 2000));
                const statusRes = await axios.get(`${API}/job-status/${jobId}`);
                if (statusRes.data.status === 'completed' && statusRes.data.image_base64) {
                  const newBg = `data:image/png;base64,${statusRes.data.image_base64}`;
                  setSceneData(prev => ({ ...prev, background_image: newBg }));
                  return newBg;
                }
                if (statusRes.data.status === 'failed') break;
              }
              return null;
            } catch { return null; }
          }}
        />

        {/* PAUSE Overlay */}
        {isPaused && !gameOver && (
          <div className="absolute inset-0 z-30 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} data-testid="pause-overlay">
            <div className="text-center">
              <h2 className="text-5xl font-bold text-yellow-400 mb-6" style={{ fontFamily: "'Rajdhani', sans-serif" }}>PAUSED</h2>
              <button
                data-testid="resume-game-btn"
                onClick={togglePause}
                className="w-64 py-3 rounded-full text-white font-bold text-lg uppercase tracking-wider"
                style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)', fontFamily: "'Rajdhani', sans-serif" }}
              >
                RESUME
              </button>
            </div>
          </div>
        )}

        {/* GAME OVER Overlay (brief, transitions to PostGameSummary) */}
        {gameOver && !showPostGameSummary && (
          <div className="absolute inset-0 z-30 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)' }} data-testid="game-over-overlay">
            <div className="text-center">
              <h2 className="text-5xl font-bold text-red-500 mb-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {isVictory ? 'VICTORY!' : 'GAME OVER'}
              </h2>
              <p className="text-2xl text-white mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>SCORE: {score}</p>
              <p className="text-sm text-cyan-400 mb-4 uppercase tracking-wider">{gameTitle || 'PLANET Q'}</p>
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto" />
            </div>
          </div>
        )}

        {/* Post-Game Summary (full share experience) */}
        {showPostGameSummary && (
          <PostGameSummary
            score={score}
            gameTitle={gameTitle}
            gameMode={gameMode}
            screenshotDataUrl={screenshotDataUrl}
            onPlayAgain={restartGame}
            onBackToMenu={backToMenu}
            isVictory={isVictory}
          />
        )}

        {/* D-pad controls (LEFT side) — hidden when game over */}
        <div className="absolute left-3 bottom-4 flex flex-col items-center gap-1 z-20" style={{ display: gameOver ? 'none' : 'flex' }}>
          {ctrl.up && (
            <button
              data-testid="ctrl-up"
              onTouchStart={(e) => { e.preventDefault(); handleMoveUp(); }}
              onClick={handleMoveUp}
              className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.2)' }}
            >
              <ArrowUp className="w-4 h-4 text-white/80" />
              <span className="text-white/50 text-[7px]">{ctrl.up}</span>
            </button>
          )}
          <div className="flex gap-1">
            {ctrl.left && (
              <button
                data-testid="ctrl-left"
                onTouchStart={(e) => { e.preventDefault(); if(window.gameActions?.moveLeft) window.gameActions.moveLeft(); }}
                className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90"
                style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.2)' }}
              >
                <span className="text-white/80 text-sm">&#8592;</span>
              </button>
            )}
            {ctrl.right && (
              <button
                data-testid="ctrl-right"
                onTouchStart={(e) => { e.preventDefault(); if(window.gameActions?.moveRight) window.gameActions.moveRight(); }}
                className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90"
                style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.2)' }}
              >
                <span className="text-white/80 text-sm">&#8594;</span>
              </button>
            )}
          </div>
          {ctrl.down && (
            <button
              data-testid="ctrl-down"
              onTouchStart={(e) => { e.preventDefault(); handleMoveDown(); }}
              onClick={handleMoveDown}
              className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.2)' }}
            >
              <ArrowDown className="w-4 h-4 text-white/80" />
              <span className="text-white/50 text-[7px]">{ctrl.down}</span>
            </button>
          )}
        </div>

        {/* Action buttons (RIGHT side) — hidden when game over */}
        {ctrl.actions && ctrl.actions.length > 0 && !gameOver && (
          <div className="absolute right-3 bottom-6 flex flex-col gap-1 items-center z-20">
            {ctrl.actions.map((action, idx) => (
              <button
                key={action}
                data-testid={`ctrl-action-${idx}`}
                onTouchStart={(e) => {
                  e.preventDefault();
                  if (idx === 0) handleShoot();
                  else if (idx === 1 && window.gameActions?.kick) window.gameActions.kick();
                }}
                onClick={() => {
                  if (idx === 0) handleShoot();
                  else if (idx === 1 && window.gameActions?.kick) window.gameActions.kick();
                }}
                className="w-11 h-11 rounded-full flex flex-col items-center justify-center active:scale-90"
                style={{
                  background: idx === 0 ? 'rgba(0,200,255,0.25)' : 'rgba(255,80,80,0.25)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <Crosshair className="w-4 h-4 text-white/80" />
                <span className="text-white/60 text-[7px]">{action}</span>
              </button>
            ))}
          </div>
        )}

        {/* Combat 6-button layout (RIGHT side) */}
        {ctrl.combatButtons && !gameOver && (
          <div className="absolute right-2 bottom-3 z-20" data-testid="combat-buttons">
            {/* Top row: LP MP HP */}
            <div className="flex gap-1 mb-1">
              {[
                { label: 'LP', color: 'rgba(0,180,255,0.3)', border: 'rgba(0,180,255,0.5)', fn: 'lightPunch' },
                { label: 'MP', color: 'rgba(0,140,255,0.35)', border: 'rgba(0,140,255,0.55)', fn: 'mediumPunch' },
                { label: 'HP', color: 'rgba(0,100,255,0.4)', border: 'rgba(0,100,255,0.6)', fn: 'hardPunch' },
              ].map(btn => (
                <button
                  key={btn.label}
                  data-testid={`combat-${btn.label.toLowerCase()}`}
                  onTouchStart={(e) => { e.preventDefault(); if (window.gameActions?.[btn.fn]) window.gameActions[btn.fn](); }}
                  onClick={() => { if (window.gameActions?.[btn.fn]) window.gameActions[btn.fn](); }}
                  className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90 transition-transform"
                  style={{ background: btn.color, border: `2px solid ${btn.border}`, backdropFilter: 'blur(6px)' }}
                >
                  <span className="text-white font-bold text-[10px]" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{btn.label}</span>
                </button>
              ))}
            </div>
            {/* Bottom row: LK MK HK */}
            <div className="flex gap-1">
              {[
                { label: 'LK', color: 'rgba(255,120,0,0.3)', border: 'rgba(255,120,0,0.5)', fn: 'lightKick' },
                { label: 'MK', color: 'rgba(255,80,0,0.35)', border: 'rgba(255,80,0,0.55)', fn: 'mediumKick' },
                { label: 'HK', color: 'rgba(255,40,0,0.4)', border: 'rgba(255,40,0,0.6)', fn: 'hardKick' },
              ].map(btn => (
                <button
                  key={btn.label}
                  data-testid={`combat-${btn.label.toLowerCase()}`}
                  onTouchStart={(e) => { e.preventDefault(); if (window.gameActions?.[btn.fn]) window.gameActions[btn.fn](); }}
                  onClick={() => { if (window.gameActions?.[btn.fn]) window.gameActions[btn.fn](); }}
                  className="w-10 h-10 rounded-full flex flex-col items-center justify-center active:scale-90 transition-transform"
                  style={{ background: btn.color, border: `2px solid ${btn.border}`, backdropFilter: 'blur(6px)' }}
                >
                  <span className="text-white font-bold text-[10px]" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{btn.label}</span>
                </button>
              ))}
            </div>
            {/* Special button */}
            <div className="flex justify-center mt-1">
              <button
                data-testid="combat-special"
                onTouchStart={(e) => { e.preventDefault(); if (window.gameActions?.special) window.gameActions.special(); }}
                onClick={() => { if (window.gameActions?.special) window.gameActions.special(); }}
                className="px-4 py-1.5 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{ background: 'rgba(255,215,0,0.3)', border: '2px solid rgba(255,215,0,0.5)', backdropFilter: 'blur(6px)' }}
              >
                <span className="text-yellow-300 font-bold text-[10px] tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>SPECIAL</span>
              </button>
            </div>
          </div>
        )}

        {/* Top bar — hidden when game over */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start z-20" style={{ display: gameOver ? 'none' : 'flex' }}>
          <div className="px-4 py-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.6)' }}>
            <span className="text-[10px] text-cyan-400 uppercase tracking-wider">
              {gameTitle || 'PLANET Q'}
            </span>
            <p className="text-lg font-bold text-white" data-testid="score-display">SCORE: {score}</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="pause-game-btn"
              onClick={togglePause}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1"
              style={{ background: isPaused ? 'rgba(255,200,0,0.5)' : 'rgba(255,255,255,0.15)' }}
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              {isPaused ? 'RESUME' : 'PAUSE'}
            </button>
            <button
              data-testid="toggle-music-btn"
              onClick={toggleMusic}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1"
              style={{ background: musicPlaying ? 'rgba(0,240,255,0.4)' : 'rgba(255,255,255,0.15)' }}
            >
              {musicPlaying ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              {musicLoading ? 'LOADING...' : (musicPlaying ? 'MUSIC ON' : 'MUSIC OFF')}
            </button>
            <button
              data-testid="save-game-btn"
              onClick={saveGame}
              disabled={savingGame}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1"
              style={{ background: 'rgba(0,150,255,0.5)' }}
            >
              {savingGame ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              SAVE
            </button>
            <button
              data-testid="restart-game-btn"
              onClick={restartGame}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold"
              style={{ background: 'rgba(0,200,100,0.5)' }}
            >
              RESTART
            </button>
            <button
              data-testid="exit-game-btn"
              onClick={backToMenu}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold"
              style={{ background: 'rgba(255,0,0,0.5)' }}
            >
              EXIT
            </button>
          </div>
        </div>

        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 text-center">
          <p className="text-white/30 text-[10px]">© Planet Q Productions</p>
        </div>

        {/* Share Link Modal */}
        {savedGameUrl && (
          <div className="absolute inset-0 z-30 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="p-6 rounded-xl max-w-sm w-full mx-4" style={{ background: '#0a1628', border: '1px solid rgba(0,240,255,0.3)' }} data-testid="share-modal">
              <div className="flex items-center gap-2 mb-3">
                <Link className="w-5 h-5 text-cyan-400" />
                <h3 className="text-white font-bold">Game Saved!</h3>
              </div>
              <p className="text-gray-400 text-sm mb-3">Share this link to let others play your game:</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={savedGameUrl}
                  className="flex-1 px-3 py-2 rounded-md text-white text-xs"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="share-link-input"
                />
                <button
                  onClick={copyShareLink}
                  className="px-3 py-2 rounded-md text-white text-sm"
                  style={{ background: 'rgba(0,240,255,0.2)', border: '1px solid rgba(0,240,255,0.4)' }}
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={() => setSavedGameUrl(null)}
                className="mt-4 w-full py-2 rounded-lg text-white text-sm font-bold"
                style={{ background: 'rgba(255,255,255,0.1)' }}
                data-testid="close-share-modal-btn"
              >
                CLOSE
              </button>
            </div>
          </div>
        )}

        {/* Exit Confirmation Modal (for non-dev users) */}
        {showExitConfirm && (
          <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="exit-confirm-overlay">
            <div className="p-6 rounded-xl max-w-sm w-full mx-4" style={{ background: '#0a1628', border: '1px solid rgba(255,200,0,0.4)' }} data-testid="exit-confirm-modal">
              <div className="flex items-center gap-2 mb-3">
                <Save className="w-5 h-5 text-yellow-400" />
                <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Save Before Exiting?</h3>
              </div>
              <p className="text-gray-300 text-sm mb-1">
                If you exit without saving, your game will be lost.
              </p>
              <p className="text-yellow-400 text-sm font-semibold mb-5">
                You will need to purchase again to generate a new game.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  data-testid="exit-save-btn"
                  onClick={() => {
                    setShowExitConfirm(false);
                    saveGame();
                  }}
                  className="w-full py-3 rounded-full text-white font-bold text-sm uppercase tracking-wider"
                  style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #0088AA 100%)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  SAVE GAME FIRST
                </button>
                <button
                  data-testid="exit-without-save-btn"
                  onClick={confirmExit}
                  className="w-full py-3 rounded-full text-white font-bold text-sm uppercase tracking-wider"
                  style={{ background: 'rgba(255,50,50,0.3)', border: '1px solid rgba(255,50,50,0.5)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  EXIT WITHOUT SAVING
                </button>
                <button
                  data-testid="exit-cancel-btn"
                  onClick={() => setShowExitConfirm(false)}
                  className="w-full py-2 rounded-full text-white/50 text-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Generator UI
  return (
    <div 
      className="min-h-screen flex flex-col relative"
      style={{ background: '#060610' }}
    >
      {/* Animated starfield background */}
      <div className="starfield">
        <div className="stars-layer stars-sm" />
        <div className="stars-layer stars-md" />
        <div className="stars-layer stars-lg" />
      </div>

      {/* Content (above stars) */}
      <div className="relative z-10 min-h-screen flex flex-col">
      {/* Header */}
      <header className="py-4 text-center">
        <h1 
          className="text-2xl md:text-3xl font-bold text-white tracking-wide"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          PLANET Q AI VIDEO GAMES
        </h1>
        <p className="text-cyan-400 text-xs tracking-wider">BY PLANET Q PRODUCTIONS</p>
      </header>

      {/* Dev Login Modal */}
      {/* Upgrade Modal (insufficient credits) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="p-6 rounded-xl max-w-sm w-full mx-4" style={{ background: '#0a1628', border: '1px solid rgba(255,180,0,0.4)' }} data-testid="upgrade-modal">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-yellow-400" />
              <h3 className="text-white font-bold text-base">More Credits Needed</h3>
            </div>
            <p className="text-white/70 text-sm mb-4">
              You don&apos;t have enough credits for this generation. Upgrade your plan on Quayla to get more credits.
            </p>
            <div className="flex gap-2">
              <a
                href={upgradeUrl || `${QUAYLA_URL}/billing/upgrade`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2 rounded-md text-black text-sm font-bold text-center"
                style={{ background: 'linear-gradient(135deg, #ffd700, #ffaa00)' }}
                data-testid="upgrade-btn"
              >
                UPGRADE PLAN
              </a>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 rounded-md text-white/50 text-sm"
                style={{ background: 'rgba(255,255,255,0.05)' }}
                data-testid="upgrade-cancel-btn"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-4">
        {/* Preview Area */}
        <div 
          className="relative rounded-lg overflow-hidden"
          style={(!loading && !sceneData) ? {} : { height: '35vh', minHeight: '200px' }}
        >
          {loading ? (
            <QuaylaLoader currentStep={loadingStep} totalSteps={3} />
          ) : sceneData ? (
            <div className="relative w-full h-full">
              {sceneData.background_image && (
                <img src={sceneData.background_image} alt="Generated" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center">
                <h1
                  className="text-4xl font-bold text-white mb-4 tracking-wider text-center px-4"
                  style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 20px rgba(0,240,255,0.6), 0 2px 8px rgba(0,0,0,0.8)' }}
                  data-testid="game-title-display"
                >
                  {gameTitle || 'PLANET Q'}
                </h1>
                <p className="text-cyan-400 text-sm uppercase tracking-widest mb-4">{gameTitle || 'PLANET Q'}</p>
                <div className="flex items-center gap-6">
                  {sceneData.character_image && (
                    <img src={sceneData.character_image} alt="Character" className="w-24 h-24 object-contain" style={{ filter: 'drop-shadow(0 0 10px #fff)' }} />
                  )}
                  {sceneData.enemy_image && (
                    <img src={sceneData.enemy_image} alt="Enemy" className="w-20 h-20 object-contain" style={{ filter: 'drop-shadow(0 0 10px #f00)' }} />
                  )}
                </div>
              </div>
              <div className="absolute left-1/2 bottom-4 -translate-x-1/2 flex gap-3">
                <button
                  onClick={startGame}
                  data-testid="play-now-btn"
                  className="px-8 py-3 rounded-full text-white font-bold uppercase"
                  style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #0088AA 100%)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  ▶ PLAY NOW
                </button>
                <button
                  onClick={saveGame}
                  disabled={savingGame}
                  data-testid="save-preview-btn"
                  className="px-4 py-3 rounded-full text-white font-bold uppercase flex items-center gap-2"
                  style={{ background: 'rgba(0,150,255,0.5)', backdropFilter: 'blur(8px)' }}
                >
                  {savingGame ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  SAVE
                </button>
              </div>
            </div>
          ) : sceneData ? (
            <div className="relative w-full h-full">
              {sceneData.background_image && (
                <img src={sceneData.background_image} alt="Generated" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center">
                <h1
                  className="text-4xl font-bold text-white mb-4 tracking-wider text-center px-4"
                  style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 20px rgba(0,240,255,0.6), 0 2px 8px rgba(0,0,0,0.8)' }}
                  data-testid="game-title-display"
                >
                  {gameTitle || 'PLANET Q'}
                </h1>
                <p className="text-cyan-400 text-sm uppercase tracking-widest mb-4">{gameTitle || 'PLANET Q'}</p>
                <div className="flex items-center gap-6">
                  {sceneData.character_image && (
                    <img src={sceneData.character_image} alt="Character" className="w-24 h-24 object-contain" style={{ filter: 'drop-shadow(0 0 10px #fff)' }} />
                  )}
                  {sceneData.enemy_image && (
                    <img src={sceneData.enemy_image} alt="Enemy" className="w-20 h-20 object-contain" style={{ filter: 'drop-shadow(0 0 10px #f00)' }} />
                  )}
                </div>
              </div>
              <div className="absolute left-1/2 bottom-4 -translate-x-1/2 flex gap-3">
                <button
                  onClick={startGame}
                  data-testid="play-now-btn"
                  className="px-8 py-3 rounded-full text-white font-bold uppercase"
                  style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #0088AA 100%)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  ▶ PLAY NOW
                </button>
                <button
                  onClick={saveGame}
                  disabled={savingGame}
                  data-testid="save-preview-btn"
                  className="px-4 py-3 rounded-full text-white font-bold uppercase flex items-center gap-2"
                  style={{ background: 'rgba(0,150,255,0.5)', backdropFilter: 'blur(8px)' }}
                >
                  {savingGame ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  SAVE
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full rounded-lg overflow-hidden" style={{ border: '1px solid rgba(0,240,255,0.3)' }}>
              <video
                ref={initVideo}
                src="/hero-video.mp4"
                preload="auto"
                autoPlay
                muted
                playsInline
                onCanPlay={() => setVideoReady(true)}
                onPlaying={() => setVideoReady(true)}
                onEnded={() => { setVideoEnded(true); setShowDirections(true); }}
                className="w-full rounded-lg"
                style={{ pointerEvents: 'none', display: 'block' }}
              />
              {videoReady && (
                <div className="absolute bottom-3 right-3 z-20 flex flex-col items-center gap-1">
                  <button
                    onClick={toggleMute}
                    data-testid="hero-video-mute-btn"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}
                  >
                    {videoMuted ? (
                      <VolumeX className="w-4 h-4 text-white/60" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-cyan-400" />
                    )}
                  </button>
                  <span className="text-white/40 text-[9px] uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Restart</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Futuristic Directions Overlay ── */}
        {showDirections && !loading && !sceneData && (
          <div
            data-testid="directions-overlay"
            className="mt-4 relative rounded-lg overflow-hidden"
            style={{
              background: 'rgba(0,10,25,0.75)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0,240,255,0.2)',
              boxShadow: '0 0 30px rgba(0,240,255,0.08), inset 0 1px 0 rgba(0,240,255,0.1)',
            }}
          >
            {/* Scan line effect */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 0.04 }}>
              {Array.from({ length: 40 }).map((_, i) => (
                <div key={i} className="w-full h-px" style={{ background: '#00F0FF', marginTop: '8px' }} />
              ))}
            </div>

            <div className="relative z-10 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-cyan-400 text-xs uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Mission Briefing
                  </span>
                </div>
                <button
                  onClick={() => setShowDirections(false)}
                  data-testid="close-directions-btn"
                  className="text-white/30 hover:text-white/60 text-xs uppercase tracking-wider px-3 py-1 rounded-full transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  Dismiss
                </button>
              </div>

              <div className="space-y-3">
                {[
                  { num: '01', text: 'To get started, scroll down below' },
                  { num: '02', text: 'Select a game mode' },
                  { num: '03', text: 'Enter your custom prompts to create your world' },
                  { num: '04', text: "Don't want custom prompts? Just enter a donation amount and we'll use our presets" },
                  { num: '05', text: 'Or click the bottom capsule and let Quayla generate a game for you' },
                ].map((step) => (
                  <div key={step.num} className="flex items-start gap-3 group">
                    <span
                      className="text-cyan-400/60 text-xs font-mono mt-0.5 shrink-0"
                      style={{ fontFamily: "'Courier New', monospace" }}
                    >
                      {step.num}
                    </span>
                    <div className="w-px h-4 mt-1 shrink-0" style={{ background: 'rgba(0,240,255,0.2)' }} />
                    <p className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {step.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(0,240,255,0.1)' }}>
                <p className="text-cyan-400/40 text-[10px] uppercase tracking-widest text-center" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  Planet Q AI World Builder — Powered by Quayla
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Game Settings Panel */}
        <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(0,20,40,0.8)', border: '1px solid rgba(0,240,255,0.3)' }}>

          {/* Game Mode Selection */}
          <div className="mb-4">
            <label className="text-xs text-cyan-400 uppercase tracking-wider mb-2 block">Game Mode</label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'run', name: 'Runner', desc: 'Endless Runner Style Game', icon: PersonStanding, color: '#00F0FF' },
                { id: 'fly', name: 'Flight', desc: 'Ace Combat Style Game', icon: Plane, color: '#39FF14' },
                { id: 'combat', name: 'Combat', desc: 'Street Fighter Style Game', icon: Swords, color: '#FF0055' },
                { id: 'race', name: 'Racer', desc: 'Racing Style Game', icon: Car, color: '#FF8800' },
                { id: 'openworld', name: 'Open World', desc: '3D Explore Style Game', icon: Globe, color: '#AA55FF' },
                { id: 'puzzle', name: 'Puzzler', desc: 'Match-3 Style Game', icon: Puzzle, color: '#FFDD00' },
              ].map((mode) => {
                const Icon = mode.icon;
                const isSelected = gameMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    data-testid={`mode-${mode.id}`}
                    onClick={() => selectMode(mode.id)}
                    className={`py-2 px-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      isSelected ? 'ring-2' : ''
                    }`}
                    style={{
                      flex: '1 1 30%',
                      minWidth: '90px',
                      background: isSelected ? `${mode.color}20` : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isSelected ? mode.color : 'rgba(255,255,255,0.1)'}`,
                      ringColor: mode.color,
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: mode.color }} />
                    <span className="text-white text-xs font-bold">{mode.name}</span>
                    <span className="text-gray-400 text-[9px]">{mode.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Features List */}
          <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                <span className="text-gray-300">AI gameplay</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-gray-300">Procedural AI enemies</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                <span className="text-gray-300">Cinematic camera</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                <span className="text-gray-300">Photorealistic render</span>
              </div>
            </div>
          </div>

          {/* Prompt Inputs */}
          <div className="space-y-3">
            {/* Game Title & Your Name — side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Game Title</label>
                <input
                  type="text"
                  value={gameTitle}
                  onChange={(e) => setGameTitle(e.target.value)}
                  placeholder={getPlaceholder('title') || 'My Awesome Game...'}
                  className="w-full px-3 py-2 rounded-md text-white text-sm outline-none placeholder:text-white/20 placeholder:italic"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="input-game-title"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Your Name</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder={getPlaceholder('name') || 'Enter your name...'}
                  className="w-full px-3 py-2 rounded-md text-white text-sm outline-none placeholder:text-white/20 placeholder:italic"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="input-player-name"
                />
              </div>
            </div>
            {[
              { label: 'Environment', value: envPrompt, setter: setEnvPrompt, field: 'env' },
              { label: 'Character', value: charPrompt, setter: setCharPrompt, field: 'char' },
              { label: 'Enemies', value: enemyPrompt, setter: setEnemyPrompt, field: 'enemy' },
              { label: 'Obstacles', value: obstaclePrompt, setter: setObstaclePrompt, field: 'obstacle' },
            ].map((f) => (
              <div key={f.label}>
                <label className="text-xs text-gray-400 mb-1 block">{f.label}</label>
                <input
                  type="text"
                  value={f.value}
                  onChange={(e) => f.setter(e.target.value)}
                  placeholder={getPlaceholder(f.field) || `${f.label}...`}
                  className="w-full px-3 py-2 rounded-md text-white text-sm outline-none placeholder:text-white/20 placeholder:italic"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`input-${f.label.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-3 px-3 py-2 rounded-lg text-sm text-red-300" style={{ background: 'rgba(220,50,80,0.2)' }}>
              {error}
            </div>
          )}

          {sceneData && (
            <button onClick={resetScene} className="w-full mt-2 text-gray-400 hover:text-white text-sm">
              Create New World
            </button>
          )}
        </div>

        {/* ── Generate Capsule (OUTSIDE the settings box) ── */}
        <div className="mt-4 px-4">
          {!isLoggedIn ? (
            /* ── Not logged in: show login button ── */
            <button
              onClick={loginWithQuayla}
              data-testid="login-btn"
              className="w-full py-3 rounded-full text-white font-bold text-base uppercase tracking-wider flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(180deg, #4a90d9 0%, #2d5a8a 100%)',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              <LogIn className="w-5 h-5" />
              LOG IN WITH QUAYLA TO GENERATE
            </button>
          ) : (
            /* ── Logged in: show generate button with credits ── */
            <div
              className="rounded-full overflow-hidden"
              style={{
                background: loading ? '#4a5568' : 'linear-gradient(180deg, #4a90d9 0%, #2d5a8a 100%)',
              }}
            >
              <button
                onClick={generateScene}
                disabled={loading}
                data-testid="generate-btn"
                className="w-full py-3 text-white font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-2"
                style={{ fontFamily: "'Rajdhani', sans-serif", background: 'transparent' }}
              >
                {loading ? 'GENERATING...' : isAdmin ? 'GENERATE YOUR WORLD' : `GENERATE YOUR WORLD (10 CREDITS)`}
              </button>
            </div>
          )}

          {/* Credits display for logged-in users */}
          {isLoggedIn && (
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <CreditCard className="w-3 h-3 text-cyan-400" />
                <span className="text-cyan-300" data-testid="credits-display">
                  {isAdmin ? 'Admin (Unlimited)' : `${quaylaUser?.credits?.remaining ?? '...'} credits remaining`}
                </span>
                <span className="text-white/30">|</span>
                <span className="text-white/40">{quaylaUser?.subscription?.plan || 'free'} plan</span>
              </div>
              <button
                onClick={logout}
                data-testid="logout-btn"
                className="text-xs text-white/30 hover:text-white/60 flex items-center gap-1 transition-colors"
              >
                <LogOut className="w-3 h-3" />
                Sign out
              </button>
            </div>
          )}

          {/* Let Quayla Generate */}
          {isLoggedIn && (
            <button
              onClick={letQuaylaGenerate}
              disabled={loading}
              data-testid="quayla-generate-btn"
              className="w-full mt-3 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
              style={{
                background: 'rgba(0,240,255,0.08)',
                border: '1px solid rgba(0,240,255,0.25)',
                color: '#00d4ff',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              <img src="/quayla-avatar.jpg" alt="" className="w-6 h-6 rounded-full" style={{ border: '1px solid rgba(0,240,255,0.4)' }} />
              LET QUAYLA GENERATE YOU A GAME?
            </button>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-3 text-center">
        <p className="text-gray-300 text-sm">Created by Planet Q Productions</p>
        <p className="text-gray-500 text-xs">© {new Date().getFullYear()} Planet Q Productions. All Rights Reserved.</p>
        {isLoggedIn && quaylaUser?.user?.name && (
          <p className="text-cyan-400/50 text-[10px] mt-1" data-testid="user-display">
            Signed in as {quaylaUser.user.name}
          </p>
        )}
      </footer>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>

      {/* Share Link Modal (generator view) */}
      {savedGameUrl && !isPlaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="p-6 rounded-xl max-w-sm w-full mx-4" style={{ background: '#0a1628', border: '1px solid rgba(0,240,255,0.3)' }} data-testid="share-modal-main">
            <div className="flex items-center gap-2 mb-3">
              <Link className="w-5 h-5 text-cyan-400" />
              <h3 className="text-white font-bold">Game Saved!</h3>
            </div>
            <p className="text-gray-400 text-sm mb-3">Share this link to let others play your game:</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={savedGameUrl}
                className="flex-1 px-3 py-2 rounded-md text-white text-xs"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="share-link-input-main"
              />
              <button
                onClick={copyShareLink}
                className="px-3 py-2 rounded-md text-white text-sm"
                style={{ background: 'rgba(0,240,255,0.2)', border: '1px solid rgba(0,240,255,0.4)' }}
                data-testid="copy-link-btn-main"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => setSavedGameUrl(null)}
              className="mt-4 w-full py-2 rounded-lg text-white text-sm font-bold"
              style={{ background: 'rgba(255,255,255,0.1)' }}
              data-testid="close-share-modal-btn-main"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
